import { useEffect, useState } from 'react';
import { useCompanyType } from 'utils/hooks';
import { nhost } from 'utils/nhost';
import { StructureNode } from '../Assessment/Structure';

export const useReportProgress = (assessmentId: string) => {
  const { isGroup } = useCompanyType();
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    const res = await nhost.functions.call('esrs/progress/company-progress', {
      assessmentId,
      type: isGroup ? 'group' : 'company',
    });

    if (!res.error) {
      setProgress((res.res?.data as { progress: string })?.progress);
    }
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, assessmentId]);
  return progress;
};

export const useDisclosureProgress = ({
  drRef,
  esrsAssessmentId,
  standardRef,
  parentAssessmentId,
}: {
  drRef: string;
  esrsAssessmentId: string;
  standardRef: string;
  parentAssessmentId?: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.call('esrs/progress/disclosure-requirement-progress', {
      drRef,
      assessmentId: esrsAssessmentId,
      standardRef,
      parentAssessmentId,
      type: isGroup ? 'group' : 'company',
    });
    if (!res.error) {
      setProgress((res.res?.data as { progress: string })?.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [drRef, esrsAssessmentId, standardRef, parentAssessmentId, isGroup]);

  return {
    progress,
    loading,
  };
};

export const useStandardProgress = ({
  standardRef,
  selectedStandardDRs,
  esrsAssessmentId,
}: {
  standardRef: string;
  selectedStandardDRs: StructureNode[];
  esrsAssessmentId: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.call('esrs/progress/standard-progress', {
      drRefs: selectedStandardDRs.map((dr) => dr.key ?? ''),
      assessmentId: esrsAssessmentId,
      standardRef: standardRef,
      type: isGroup ? 'group' : 'company',
    });

    if (!res.error) {
      setProgress((res.res?.data as { progress: string })?.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, selectedStandardDRs, esrsAssessmentId, standardRef]);

  return {
    progress,
    loading,
  };
};

export const useCategoryProgress = ({
  categoryRef,
  esrsAssessmentId,
}: {
  categoryRef: string;
  esrsAssessmentId: string;
}) => {
  const { isGroup } = useCompanyType();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('0');

  const getProgress = async () => {
    setLoading(true);
    const res = await nhost.functions.call('esrs/progress/category-progress', {
      assessmentId: esrsAssessmentId,
      categoryRef: categoryRef ?? '',
      type: isGroup ? 'group' : 'company',
    });

    if (!res.error) {
      setProgress((res.res?.data as { progress: string })?.progress);
    }
    setLoading(false);
  };

  useEffect(() => {
    getProgress();
  }, [isGroup, esrsAssessmentId, categoryRef]);

  return {
    progress,
    loading,
  };
};
